/**
 * SSE-first notifications client for queue-based reporting.
 * Mirrors minimal client contract: EventSource `/notifications/{sessionId}` with polling fallback.
 */

const DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta && import.meta.env && import.meta.env.VITE_API_URL) ||
  (typeof window !== 'undefined' && window.API_URL) ||
  '';

export const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  FALLBACK_POLLING: 'fallback_polling'
};

export const EventType = {
  ZONE_COMPLETE: 'zone_complete',
  ZONE_ERROR: 'zone_error',
  REPORT_FINAL: 'report_final',
  PROGRESS: 'progress'
};

const STATUS_PRIORITY = ['failed', 'processing', 'queued', 'pending', 'completed'];

function normalizeBase(url) {
  if (!url) return '';
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function safeParse(data) {
  try {
    return JSON.parse(data);
  } catch (err) {
    console.warn('sseClient: failed to parse event', err);
    return null;
  }
}

export function createNotificationsClient(options = {}) {
  return new NotificationsClient(options);
}

export function getNotificationsClient() {
  if (!globalThis.__homeNotificationsClient) {
    globalThis.__homeNotificationsClient = new NotificationsClient();
  }
  return globalThis.__homeNotificationsClient;
}

class NotificationsClient {
  constructor({
    apiBase = DEFAULT_BASE,
    pollingInterval = 4000,
    reconnectDelays = [1000, 2000, 4000, 8000]
  } = {}) {
    this.apiBase = normalizeBase(apiBase);
    this.pollingInterval = pollingInterval;
    this.reconnectDelays = reconnectDelays;

    this.sessionId = null;
    this.frontendId = null;
    this.userId = null;
    this.eventSource = null;
    this.pollTimer = null;
    this.reconnectTimer = null;
    this.retryIndex = 0;
    this.state = ConnectionState.DISCONNECTED;
    this.listeners = new Map();

    this.reportState = {
      zones: {},
      totalZones: 0,
      completedZones: 0,
      failedZones: 0,
      progress: 0,
      draftReportUrl: null,
      finalReportUrl: null
    };
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    const list = this.listeners.get(event);
    if (!list) return;
    const idx = list.indexOf(callback);
    if (idx >= 0) list.splice(idx, 1);
  }

  emit(event, payload) {
    const list = this.listeners.get(event);
    if (list) {
      list.forEach((cb) => {
        try {
          cb(payload);
        } catch (err) {
          console.error('sseClient listener error', err);
        }
      });
    }
  }

  getState() {
    return this.state;
  }

  getProgress() {
    return { ...this.reportState };
  }

  async connect(sessionId, frontendId = 'home-app', userId = sessionId) {
    if (!sessionId) throw new Error('sessionId is required');
    if (this.eventSource) this.disconnect();

    this.sessionId = sessionId;
    this.frontendId = frontendId;
    this.userId = userId;
    this._setState(ConnectionState.CONNECTING);

    const url = `${this.apiBase}/notifications/${encodeURIComponent(sessionId)}`;
    this.eventSource = new EventSource(url);

    this.eventSource.onopen = () => {
      this.retryIndex = 0;
      this._setState(ConnectionState.CONNECTED);
      this.emit('connected');
    };

    this.eventSource.onerror = () => {
      this.emit('error', { error: 'sse_error' });
      this._scheduleReconnect();
    };

    this.eventSource.onmessage = (evt) => {
      const data = safeParse(evt.data);
      if (!data || !data.eventType) return;
      this._handleEvent(data);
    };
  }

  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._setState(ConnectionState.DISCONNECTED);
    this.emit('disconnected', { reason: 'manual' });
  }

  destroy() {
    this.disconnect();
    this.listeners.clear();
  }

  async _handleEvent(event) {
    if (event.eventType === EventType.ZONE_COMPLETE || event.eventType === EventType.ZONE_ERROR) {
      this._updateZonesFromEvent(event);
    }

    if (event.eventType === EventType.REPORT_FINAL) {
      this.reportState.finalReportUrl =
        event.reportUrl || `${this.apiBase}/reports/${this.sessionId}`;
      this.reportState.progress = 100;
      this.emit(EventType.REPORT_FINAL, { ...event, finalReportUrl: this.reportState.finalReportUrl });
    } else {
      this.emit(event.eventType, event);
    }

    this.emit(EventType.PROGRESS, this.getProgress());
  }

  _updateZonesFromEvent(event) {
    const zoneName = event.zone || `Zone ${event.zoneIndex ?? Object.keys(this.reportState.zones).length + 1}`;
    const current = this.reportState.zones[zoneName] || { status: 'pending' };

    current.status = event.eventType === EventType.ZONE_COMPLETE ? 'completed' : 'failed';
    current.grade = event.grade || current.grade;
    current.reportUrl = event.reportUrl || current.reportUrl;
    this.reportState.zones[zoneName] = current;

    const values = Object.values(this.reportState.zones);
    this.reportState.totalZones = Math.max(this.reportState.totalZones, values.length);
    this.reportState.completedZones = values.filter((z) => z.status === 'completed').length;
    this.reportState.failedZones = values.filter((z) => z.status === 'failed').length;

    if (!this.reportState.draftReportUrl && this.reportState.completedZones > 0) {
      this.reportState.draftReportUrl = `${this.apiBase}/reports/${this.sessionId}`;
    }

    const done = this.reportState.completedZones + this.reportState.failedZones;
    const denom = this.reportState.totalZones || 1;
    this.reportState.progress = Math.min(100, Math.round(15 + (done / denom) * 85));
  }

  _setState(newState) {
    if (this.state === newState) return;
    const old = this.state;
    this.state = newState;
    this.emit('state_change', { oldState: old, newState });
  }

  _scheduleReconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    if (this.retryIndex < this.reconnectDelays.length) {
      const delay = this.reconnectDelays[this.retryIndex++];
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        if (this.sessionId && this.state !== ConnectionState.DISCONNECTED) {
          this.connect(this.sessionId, this.frontendId, this.userId).catch(() => {
            this._fallbackToPolling();
          });
        }
      }, delay);
    } else {
      this._fallbackToPolling();
    }
  }

  _fallbackToPolling() {
    if (this.pollTimer) return;
    this._setState(ConnectionState.FALLBACK_POLLING);
    this.emit('fallback', { reason: 'sse_unavailable' });

    const poll = async () => {
      try {
        const res = await fetch(`${this.apiBase}/jobs?sessionId=${encodeURIComponent(this.sessionId)}`);
        if (!res.ok) throw new Error(`jobs ${res.status}`);
        const data = await res.json();
        this._applyJobsSnapshot(data.jobs || data || []);
      } catch (err) {
        this.emit('error', { error: err.message });
      }
    };

    poll();
    this.pollTimer = setInterval(poll, this.pollingInterval);
  }

  _applyJobsSnapshot(jobs) {
    if (!Array.isArray(jobs)) return;
    jobs.forEach((job, idx) => {
      const zoneName = job.zone || job.zoneName || `Zone ${idx + 1}`;
      const status = job.status || 'pending';
      const current = this.reportState.zones[zoneName] || { status: 'pending' };

      if (STATUS_PRIORITY.indexOf(status) > STATUS_PRIORITY.indexOf(current.status)) {
        current.status = status;
      }

      current.reportUrl = job.reportUrl || current.reportUrl;
      current.grade = job.grade || current.grade;
      this.reportState.zones[zoneName] = current;
    });

    const values = Object.values(this.reportState.zones);
    this.reportState.totalZones = Math.max(this.reportState.totalZones, values.length);
    this.reportState.completedZones = values.filter((z) => z.status === 'completed').length;
    this.reportState.failedZones = values.filter((z) => z.status === 'failed').length;

    if (!this.reportState.draftReportUrl && this.reportState.completedZones > 0) {
      this.reportState.draftReportUrl = `${this.apiBase}/reports/${this.sessionId}`;
    }

    const done = this.reportState.completedZones + this.reportState.failedZones;
    const denom = this.reportState.totalZones || 1;
    this.reportState.progress = Math.min(100, Math.round(15 + (done / denom) * 85));

    this.emit(EventType.PROGRESS, this.getProgress());
    const allDone = this.reportState.totalZones > 0
      && (this.reportState.completedZones + this.reportState.failedZones) >= this.reportState.totalZones;
    if (allDone && !this.reportState.finalReportUrl) {
      this.reportState.progress = 100;
      this.reportState.finalReportUrl = `${this.apiBase}/reports/${encodeURIComponent(this.sessionId)}`;
      this.emit(EventType.REPORT_FINAL, { reportUrl: this.reportState.finalReportUrl });
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}

export default NotificationsClient;
